import {
  faCheck,
  faDownload,
  faExclamationCircle,
  faPencil,
  faSpinnerThird,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useCallback } from 'react';

import type { Campaign, PartnerExport, TPartnerExportState } from '@feathr/blackbox';
import { CampaignClass } from '@feathr/blackbox';
import {
  Avatar,
  Button,
  ButtonValid,
  Card,
  Fieldset,
  Time,
  toast,
  UniversalSelect,
} from '@feathr/components';
import { formatCampaignOptionLabel } from '@feathr/extender/components/SelectOptions';
import { useStore } from '@feathr/extender/state';
import { DEFAULT_DEBOUNCE_WAIT, TimeFormat } from '@feathr/hooks';

import * as styles from './EventPartnerExportCard.css';

import noImg from 'images/no-img.png';

interface IFieldOption {
  value: string;
  label: string;
}

const fieldOptions: IFieldOption[] = [
  { value: 'external_id', label: 'External ID' },
  { value: 'name', label: 'Partner Name' },
  { value: 'email', label: 'Partner Email' },
  { value: 'description', label: 'Partner Description' },
  { value: 'website', label: 'Partner Website' },
  { value: 'logo', label: 'Partner Logo' },
  { value: 'dashboard_url', label: 'Partner Dashboard Link' },
  { value: 'partner_status', label: 'Active' },
  { value: 'profile_url', label: 'Partner Profile Link' },
  { value: 'partner_stats', label: 'Partner Stats' },
  { value: 'referral_page_links', label: 'Invite Page Links' },
  { value: 'partner_tags', label: 'Tags' },
  { value: 'partner_custom_data', label: 'Custom Data' },
];

interface IExportCardProps {
  partnerExport: PartnerExport;
}

const DownloadButton = ({ partnerExport }: IExportCardProps) => {
  return (
    <Button
      className={styles.button}
      href={`${BLACKBOX_URL}events/${partnerExport.get('event')}/partners/exports/${
        partnerExport.id
      }/download`}
      icon={<FontAwesomeIcon icon={faDownload} />}
      target={'_blank'}
    >
      Download export file
    </Button>
  );
};

const FinishButton = observer(({ partnerExport }: IExportCardProps) => {
  const { PartnerExports } = useStore();
  const fields: string[] = partnerExport.get('export_fields', []);

  async function onSave() {
    try {
      await PartnerExports.add(partnerExport);
      toast(
        'Partner export is in progress. You will be notified by email when the export is complete.',
        { type: 'success' },
      );
    } catch (e) {
      toast('There was an error creating the partner export.', { type: 'error' });
    }
  }

  const validationErrors = partnerExport.validate(
    ['event', 'export_fields', 'campaign'],
    false,
  ).errors;
  if (fields.includes('referral_page_links') && !partnerExport.get('campaign')) {
    validationErrors.push('You must choose a campaign to export referral page links.');
  }
  return (
    <ButtonValid className={styles.button} errors={validationErrors} onClick={onSave}>
      Export partners
    </ButtonValid>
  );
});
FinishButton.displayName = 'FinishButton';

function EventPartnerExportCard({ partnerExport }: IExportCardProps) {
  const { Users, Campaigns } = useStore();
  const state: TPartnerExportState = partnerExport.get('state');
  const createdBy = Users.get(partnerExport.get('created_by'));
  const rows = partnerExport.get('rows', 0);
  const exportFields: string[] = partnerExport.get('export_fields', []);
  const campaignId = partnerExport.get('campaign');
  const campaignSelectValue = campaignId ? Campaigns.get(campaignId) : undefined;

  const loadCampaignOptions = useCallback(
    async (
      inputValue: string,
      _prevOptions: readonly Campaign[],
      additional?: { page: number },
    ): Promise<{
      options: readonly Campaign[];
      hasMore: boolean;
      additional: { page: number };
    }> => {
      const page = additional?.page ?? 0;
      const campaigns = Campaigns.list({
        filters: {
          _cls: CampaignClass.Referral,
          event: partnerExport.get('event'),
          is_archived__ne: true,
          ...(inputValue ? { name__icontains: inputValue } : {}),
        },
        pagination: { page, page_size: UniversalSelect.DEFAULT_PAGE_SIZE },
      });
      await when(() => !campaigns.isPending);

      return {
        options: campaigns.models,
        hasMore: page + 1 < campaigns.pagination.pages,
        additional: { page: page + 1 },
      };
    },
    [Campaigns, partnerExport],
  );

  function handleSelectCampaign(option: Campaign): void {
    partnerExport.set({ campaign: option.id });
  }

  function getCampaignOptionLabel(option: Campaign): string {
    return option.get('name');
  }

  function handleSelectFields(options: IFieldOption[]): void {
    partnerExport.set({ export_fields: options.map((o) => o.value) });
  }

  const exportFieldSelectValues = fieldOptions.filter((option) =>
    exportFields.includes(option.value),
  );

  const helpText = state
    ? ''
    : 'It is only necessary to choose a campaign if you want to export invite page links. If chosen, the export will include only partners that are participating in the chosen campaign.';

  return (
    <Card className={styles.card}>
      <div className={styles.meta}>
        <Avatar image={createdBy.get('picture') || noImg} size={'large'} />
        <div className={styles.subdued}>
          {partnerExport.get('date_created') && (
            <Time format={TimeFormat.shortDate} timestamp={partnerExport.get('date_created')} />
          )}
        </div>
      </div>
      <p>
        {createdBy.name} {state ? 'exported partners.' : 'started a new export.'}
      </p>
      {rows > 0 && (
        <div className={styles.partners}>
          <b>Partners: </b>
          {partnerExport.get('rows')}
        </div>
      )}

      <FontAwesomeIcon
        className={classNames(
          styles.icon,
          { [styles.ready]: state === 'ready' },
          { [styles.failed]: state === 'failed' },
        )}
        icon={
          state
            ? {
                ready: faCheck,
                pending: faSpinnerThird,
                exporting: faSpinnerThird,
                failed: faExclamationCircle,
              }[state]
            : faPencil
        }
        spin={state === 'pending'}
      />
      <span>
        {state
          ? {
              ready: 'Ready',
              pending: 'Processing',
              exporting: 'Processing',
              failed: 'Failed',
            }[state]
          : 'Draft'}
      </span>
      <Fieldset>
        <UniversalSelect<Campaign>
          className={styles.campaigns}
          debounceTimeout={DEFAULT_DEBOUNCE_WAIT}
          defaultOptions={true}
          disabled={!!state}
          formatOptionLabel={formatCampaignOptionLabel}
          getOptionLabel={getCampaignOptionLabel}
          helpPlacement={'bottom'}
          helpText={helpText}
          label={'Campaign'}
          loadPagedOptions={loadCampaignOptions}
          name={'campaign-select'}
          onSelectSingle={handleSelectCampaign}
          optional={true}
          paginate={true}
          placeholder={'No campaign selected.'}
          value={campaignSelectValue}
        />
        <UniversalSelect<IFieldOption>
          disabled={!!partnerExport.get('state')}
          isMulti={true}
          label={'Export fields'}
          name={'export-fields'}
          onSelectMulti={handleSelectFields}
          options={fieldOptions}
          placeholder={'No fields selected.'}
          value={exportFieldSelectValues}
        />
      </Fieldset>
      {state === 'ready' && <DownloadButton partnerExport={partnerExport} />}
      {!state && <FinishButton partnerExport={partnerExport} />}
    </Card>
  );
}

export default observer(EventPartnerExportCard);
